import type { Ref } from 'vue'

import type { DraftRecordItem } from './useDraftDataManager'
import { MACRO_DELAY_DEFAULT } from '../types'

interface RecordActionsContext {
  workingData: Ref<DraftRecordItem[]>
  markAsDeleted: (index: number) => boolean
  findIndexByRef: (record: DraftRecordItem) => number
}

/**
 * 删除/新增（延迟）等基础操作。
 */
export function useRecordActions(context: RecordActionsContext) {
  const { workingData, markAsDeleted, findIndexByRef } = context

  const isVisibleDelay = (index: number) => {
    const item = workingData.value[index]
    return !!item && item.__status !== 'deleted' && item.type === 4
  }

  const deleteRecord = (record: DraftRecordItem) => {
    const index = findIndexByRef(record)
    if (index < 0) return

    const current = workingData.value[index]

    // 按键动作删除：尽量按成对 down/up 删除，并带上相邻延迟。
    if (current?.type === 1) {
      let pairedIndex = -1

      if (current.action === 1) {
        for (let cursor = index + 1; cursor < workingData.value.length; cursor++) {
          const item = workingData.value[cursor]
          if (!item || item.__status === 'deleted') continue
          if (item.type === 1 && item.keystroke === current.keystroke && item.action === 0) {
            pairedIndex = cursor
            break
          }
        }
      }

      if (current.action === 0) {
        for (let cursor = index - 1; cursor >= 0; cursor--) {
          const item = workingData.value[cursor]
          if (!item || item.__status === 'deleted') continue
          if (item.type === 1 && item.keystroke === current.keystroke && item.action === 1) {
            pairedIndex = cursor
            break
          }
        }
      }

      if (pairedIndex >= 0) {
        const start = Math.min(index, pairedIndex)
        const end = Math.max(index, pairedIndex)

        if (isVisibleDelay(start - 1)) markAsDeleted(start - 1)
        if (isVisibleDelay(end - 1)) markAsDeleted(end - 1)

        markAsDeleted(start)
        markAsDeleted(end)
        return
      }

      if (isVisibleDelay(index - 1)) markAsDeleted(index - 1)
      markAsDeleted(index)
      return
    }

    if (current?.type !== 4 && isVisibleDelay(index - 1)) {
      markAsDeleted(index - 1)
    }

    markAsDeleted(index)
  }

  /**
   * 在指定动作后新增延迟，返回新延迟的 __id 便于调用方打开输入框。
   * 与相邻延迟的合并交给保存/回显时的归一化处理，编辑期间保持独立可编辑。
   */
  const addDelayAfterRecord = (
    record: DraftRecordItem,
    insertAt: (index: number, record: DraftRecordItem) => void,
  ) => {
    const index = findIndexByRef(record)
    if (index < 0) return undefined

    insertAt(index + 1, {
      type: 4,
      time: MACRO_DELAY_DEFAULT,
    })

    return workingData.value[index + 1]?.__id
  }

  return {
    deleteRecord,
    addDelayAfterRecord,
  }
}
